import React, { useState } from 'react';
import { ExistingProfile } from '../types/profileTypes';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import colors from 'src/utils/colors';
import { SlButton, SlButtonProps } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { Divider } from '@mui/material';
import AppProfileIcon from 'src/components/BeaconV2Components/Header/AppProfileIcon';

import { useAppSelector } from 'src/utils/Hooks';
import { apiArchiveProfile } from '../api/profileApis';
import { useSnackbar } from 'src/utils/Hooks';
import { formatTime } from 'src/utils/dateformatting';
import { BEACON_SUBTABS, useBeaconRoutes } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';

interface ProfileCardProps {
  profile: ExistingProfile & { createdByName?: string };
  onProfileArchived?: () => void;
  onEditProfile?: (profileId: string) => void;
  // Selection mode props
  isSelectable?: boolean;
  isSelected?: boolean;
  onSelect?: (profileId: string) => void;
}

const Button: React.FC<SlButtonProps> = (props) => {
  return (
    <SlButton
      {...props}
      buttonSx={{
        backgroundColor: 'white',
        border: `1px solid ${colors.graysSecondary}`,
        borderRadius: '16px',
        ...props.buttonSx
      }}
      sx={{
        borderRadius: '8px',
        padding: '8px 16px',
        ...props.sx
      }}
    >
      {props.children}
    </SlButton>
  );
};

export const ProfileCard: React.FC<ProfileCardProps> = ({
  profile,
  onProfileArchived,
  onEditProfile,
  isSelectable = false,
  isSelected = false,
  onSelect
}) => {
  const { showSnackbar } = useSnackbar();
  const [isArchiving, setIsArchiving] = useState(false);
  const { currentSubtab } = useBeaconRoutes();

  const profileId = profile.profileId;

  // Auto-detect selection mode from current subtab
  const isInSelectionMode = isSelectable || currentSubtab === BEACON_SUBTABS.PROJECTS_SETTINGS;

  const handleEdit = () => {
    if (onEditProfile && !isInSelectionMode) {
      onEditProfile(profileId);
    }
  };

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(profileId);
    }
  };

  const handleArchive = async () => {
    try {
      setIsArchiving(true);
      await apiArchiveProfile(profileId);
      showSnackbar({
        message: 'Profile archived successfully.',
        type: 'success'
      });

      // Call the callback if provided
      if (onProfileArchived) {
        onProfileArchived();
      }
    } catch (error) {
      showSnackbar({
        message: 'Failed to archive profile.',
        type: 'error'
      });
    } finally {
      setIsArchiving(false);
    }
  };

  const { team } = useAppSelector((state) => state.user);
  const user = team.find((user) => user.id === profile.createdBy);

  const displayName = (() => {
    if (user?.firstName || user?.lastName) {
      return `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
    }
    if ((profile as any)?.createdByName) {
      return (profile as any).createdByName as string;
    }
    return profile.createdBy;
  })();

  const displayInitials = (() => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`;
    }
    const name = profile?.createdByName ?? profile.createdBy;
    if (name) {
      const parts = name.trim().split(/\s+/);
      const first = parts[0]?.[0] ?? '';
      const last = parts[1]?.[0] ?? '';
      return `${first}${last}` || (profile.createdBy?.[0] ?? '');
    }
    return profile.createdBy?.[0] ?? '';
  })();

  return (
    <Flex
      flexDirection="column"
      sx={{
        border: `1px solid ${isSelected ? colors.darkBlue : colors.graysSecondary}`,
        borderRadius: '8px',
        boxSizing: 'border-box',
        padding: '16px',
        width: '336px',
        height: '153px',
        backgroundColor: 'white',
        cursor: isInSelectionMode ? 'default !important' : 'pointer !important',
        transition: 'all 0.2s ease',
        ...(!isInSelectionMode && {
          '&:hover .profile-card-title': {
            textDecoration: 'underline'
          }
        })
      }}
      role="button"
      onClick={isInSelectionMode ? undefined : handleEdit}
    >
      <Text
        variant="body1"
        className="profile-card-title"
        sx={{
          fontWeight: 700,
          marginBottom: '4px',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}
      >
        {profile?.profileName}
      </Text>
      <Divider sx={{ marginTop: 'auto', marginBottom: '16px' }} />
      <Flex justifyContent="space-between" alignItems="center">
        <Flex>
          <div
            style={{
              height: '30px',
              width: '30px',
              overflow: 'hidden',
              marginRight: '8px',
              borderRadius: '50%',
              flexShrink: 0
            }}
          >
            <AppProfileIcon userId={profile?.createdBy} size={30} initials={displayInitials} />
          </div>
          <Flex flexDirection="column">
            <Text
              variant="body2"
              sx={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                marginBottom: '2px'
              }}
            >
              {displayName}
            </Text>
            <Text
              variant="body3"
              sx={{
                color: colors.grey
              }}
            >
              Updated {formatTime(profile?.updatedAt, 'MMM D, YYYY')}
            </Text>
          </Flex>
        </Flex>
        {isInSelectionMode && (
          <SlButton
            variant="normal"
            onClick={handleSelect}
            buttonSx={{
              border: `1px solid ${isSelected ? colors.darkBlue : colors.graysSecondary}`,
              borderRadius: '16px',
              padding: '3px 20px',
              height: '24px',
              width: '74px',
              backgroundColor: isSelected ? colors.darkBlue : 'white',
              minWidth: '64px',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: isSelected ? colors.darkBlue : '#F6F7F8'
              }
            }}
            textSx={{
              color: isSelected ? 'white' : colors.darkBlue,
              fontSize: '12px',
              fontWeight: 500
            }}
          >
            {isSelected ? 'Selected' : 'Select'}
          </SlButton>
        )}
      </Flex>
    </Flex>
  );
};
